import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItemText,
  Typography,
  Box,
  ListItemIcon,
  Divider,
  ListItemButton,
  Stack,
} from "@mui/material";
import {Message, Notifications as NotificationsIcon} from "@mui/icons-material";
import {Diagnosis, FrontendEvent, Prognosis, Task, Ticket} from "@core/model";
import {translate} from "@util";
import {
  DiagnosisIcon,
  PrognosisIcon,
  TaskIcon,
  TicketIcon,
} from "@components/icons";
import {useNavigate} from "react-router-dom";
import dayjs from "dayjs";
import {api} from "@core/api";
import {format} from "@constants";
import {Selectors} from "@core/store/selectors";

type Notification = FrontendEvent & {
  unread: boolean;
};

const Notifications = ({
  size = "small",
}: {
  onPrimary?: boolean;
  size?: "small" | "medium" | "large";
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const events = useSelector(Selectors.events);
  const lastEvent = useSelector(Selectors.lastEvent);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [now, setNow] = useState(new Date().getTime());

  useEffect(() => {
    if (lastEvent) {
      const payload = lastEvent.payload as Ticket;

      if (lastEvent.type === "ticket-message") {
        dispatch(api.util.invalidateTags([{type: "ticket", id: payload.id}]));
      } else if (
        lastEvent.type === "ticket-created" ||
        lastEvent.type === "ticket-updated" ||
        lastEvent.type === "ticket-status-updated"
      ) {
        dispatch(api.util.invalidateTags([{type: "ticket", id: "ALL"}]));
      } else if (
        lastEvent.type === "task-created" ||
        lastEvent.type === "task-updated"
      ) {
        dispatch(api.util.invalidateTags([{type: "task", id: "range"}]));
      } else if (lastEvent.type === "prognosis") {
        dispatch(api.util.invalidateTags([{type: "prognosis", id: "LIST"}]));
      } else if (lastEvent.type === "diagnosis") {
        dispatch(api.util.invalidateTags([{type: "diagnosis", id: "LIST"}]));
      }

      if (window.location.pathname.indexOf(payload.id) > 0) {
        // not displaing notification if element is showed in current screen
        return;
      }
    }
  }, [lastEvent, dispatch]);

  useEffect(() => {
    setNotifications(events.map(e => ({...e, unread: e.date > now})));
  }, [events, now]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setNow(new Date().getTime());
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const ticketClick = (ticket: Ticket) => {
    handleClose();
    navigate(`/dashboard/tickets/${ticket.id}`);
  };

  const taskClick = (t: Task) => {
    handleClose();
    navigate(`/dashboard/tasks/day/${t.id}`);
  };

  const prognosisClick = (p: Prognosis) => {
    handleClose();
    navigate(`/dashboard/prognosis/${p.id}`);
  };

  const diagnosisClick = (d: Diagnosis) => {
    handleClose();
    navigate(`/dashboard/diagnosis/${d.id}`);
  };

  /*const handleDelete = (index: number) => {
    setNotifications(old => {
      const newEvents = [...old];
      newEvents.splice(index, 1);
      return newEvents;
    });
  };*/

  const renderEvent = (event: FrontendEvent, index: number, last: boolean) => {
    let icon,
      text,
      onClick = () => {};
    switch (event.type) {
      case "task-created":
      case "task-updated":
        icon = <TaskIcon />;
        text = (event.payload as Task).name;
        onClick = () => taskClick(event.payload as Task);
        break;
      case "ticket-created":
      case "ticket-updated":
      case "ticket-status-updated":
        icon = <TicketIcon />;
        text = (event.payload as Ticket).name;
        onClick = () => ticketClick(event.payload as Ticket);
        break;
      case "ticket-message":
        icon = <Message />;
        text = (event.payload as Ticket).name;
        onClick = () => ticketClick(event.payload as Ticket);
        break;
      case "prognosis":
        icon = <PrognosisIcon />;
        text = (event.payload as Prognosis).name;
        onClick = () => prognosisClick(event.payload as Prognosis);
        break;
      case "diagnosis":
        icon = <DiagnosisIcon />;
        text = (event.payload as Diagnosis).name;
        onClick = () => diagnosisClick(event.payload as Diagnosis);
        break;
      default:
        break;
    }

    return (
      <Box key={index} sx={{position: "relative"}}>
        <ListItemButton onClick={onClick} divider={!last}>
          <ListItemIcon>
            <Stack alignItems="center">
              {icon}
              <Typography variant="caption" color="textSecondary">
                {dayjs(event.date).format(format.time.short)}
              </Typography>
            </Stack>
          </ListItemIcon>
          <ListItemText
            primary={translate(`notifications.${event.type}`)}
            secondary={text}
          />
        </ListItemButton>
      </Box>
    );
  };

  const unreadCounter = notifications.filter(n => n.unread).length;

  return (
    <>
      <IconButton onClick={handleClick} size={size}>
        <Badge badgeContent={unreadCounter} color="error">
          <NotificationsIcon fontSize="inherit" color="action" />
        </Badge>
      </IconButton>
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
        transformOrigin={{vertical: "top", horizontal: "right"}}>
        <Box px={2} py={1}>
          <Typography variant="h6">
            {translate("notifications.title")}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{width: 300, maxHeight: 400, overflowY: "auto"}}>
          {notifications.length === 0 && (
            <Box px={2} py={1}>
              <Typography variant="body2" color="text.secondary">
                {translate("notifications.empty")}
              </Typography>
            </Box>
          )}
          <List dense>
            {notifications
              .sort((e1, e2) => e2.date - e1.date)
              .map((event, index) =>
                renderEvent(event, index, index === notifications.length - 1),
              )}
          </List>
        </Box>
      </Popover>
    </>
  );
};

export {Notifications};
